<script setup>
import { useRoute, useRouter } from 'vue-router'
import { userAuthState } from '@/@core/auth'
import antibioticRequestService from '@/services/quality/antibioticRequest.service'
import { AntibioticRequestStatus } from '@/enums/quality/antibiotic-request-status'
import RequestHero from '@/views/quality/infection-control/antibiotic/RequestHero.vue'
import TherapyCourseCard from '@/views/quality/infection-control/antibiotic/TherapyCourseCard.vue'
import ReassessmentTimeline from '@/views/quality/infection-control/antibiotic/ReassessmentTimeline.vue'
import ClinicalContextCard from '@/views/quality/infection-control/antibiotic/ClinicalContextCard.vue'
import RegimenCard from '@/views/quality/infection-control/antibiotic/RegimenCard.vue'
import RequestRecordCard from '@/views/quality/infection-control/antibiotic/RequestRecordCard.vue'
import RequestDecisionCard from '@/views/quality/infection-control/antibiotic/RequestDecisionCard.vue'
import RequestDrawer from '@/views/quality/infection-control/antibiotic/RequestDrawer.vue'
import ReassessDialog from '@/views/quality/infection-control/antibiotic/ReassessDialog.vue'
import { capabilities, isReviewDue } from '@/views/quality/infection-control/antibiotic/antibiotic-derived'

definePage({
  meta: {
    subject: 'quality.infection_control.menu',
    action: 'antibiotic_stewardship',
    navActiveLink: 'app-quality-infection-control-antibiotic',
  },
})

const route = useRoute()
const router = useRouter()
const toast = useToast()

// `show` returns a bare Resource, so the body is `{data}` with no `meta` --
// useApi already unwraps that shape. Read `data.value`; reading `.data` again
// yields undefined (the bug fixed in e7ae38f92).
const { data, execute: fetchRequest } = await useApi(createUrl(`/infection-control/antibiotic-requests/${route.params.id}`), {
  onFetchError: () => {
    router.push({ name: 'invalid-url', params: { code: 404 } })
  },
})

const request = computed(() => data.value ?? null)

const refetchRequest = () => fetchRequest()

// REVIEW_DUE is derived, never stored on `status`. Resolve it exactly once
// here and hand the same display status to every child, so the hero and the
// decision card can never disagree about the same overdue course.
const displayStatus = computed(() =>
  (isReviewDue(request.value) ? AntibioticRequestStatus.REVIEW_DUE : request.value?.status))

const currentUserId = computed(() => userAuthState().user?.id ?? null)

// Fail closed: `capabilities` cannot enforce separation of duties without a
// user id, so when the signed-in user cannot be resolved the SoD-gated
// actions come back blocked with a reason -- never optimistically enabled.
const caps = computed(() => {
  const base = capabilities(request.value, currentUserId.value)

  if (currentUserId.value) return base

  const blockSodGated = capability => (capability.allowed
    ? { ...capability, allowed: false, reason: 'Your signed-in user could not be verified. Sign in again to review this request.' }
    : capability)

  return {
    ...base,
    approve: blockSodGated(base.approve),
    escalate: blockSodGated(base.escalate),
  }
})

// The page owns all dialog state so the hero, timeline and decision card
// share one drawer and one reassess dialog.
const isDrawerOpen = ref(false)
const drawerMode = ref('approve')
const isReassessDialogOpen = ref(false)

const openDrawer = mode => {
  drawerMode.value = mode
  isDrawerOpen.value = true
}

const openReassess = () => {
  isReassessDialogOpen.value = true
}

// The service only console.errors a failed transition, so surface it here --
// otherwise a rejected save (including the separation-of-duties rule) looks
// identical to a dead button.
const onActionError = error => {
  toast.error(error?.data?.message ?? 'Could not update the antibiotic request', 'error')
}

const startTherapy = () =>
  antibioticRequestService.startTherapy(request.value.id, {}, refetchRequest, onActionError)

const completeCourse = () =>
  antibioticRequestService.complete(request.value.id, {}, refetchRequest, onActionError)
</script>

<template>
  <section v-if="request">
    <RequestHero
      :request="request"
      :status="displayStatus"
      class="mb-6"
      @reassess="openReassess"
    />

    <VRow>
      <!-- Main column: course track, audit trail, clinical context -->
      <VCol
        cols="12"
        md="8"
        class="order-2 order-md-1"
      >
        <div class="d-flex flex-column gap-6">
          <TherapyCourseCard
            v-if="request.started_at"
            :request="request"
          />
          <ReassessmentTimeline
            :request="request"
            @reassess="openReassess"
          />
          <ClinicalContextCard :request="request" />
        </div>
      </VCol>

      <!-- Rail: the decision first, then what it is being made about -->
      <VCol
        cols="12"
        md="4"
        class="order-1 order-md-2"
      >
        <div class="d-flex flex-column gap-6 request-rail">
          <RequestDecisionCard
            :request="request"
            :status="displayStatus"
            :capabilities="caps"
            @approve="openDrawer('approve')"
            @reject="openDrawer('reject')"
            @escalate="openDrawer('escalate')"
            @start-therapy="startTherapy"
            @reassess="openReassess"
            @complete="completeCourse"
          />
          <RegimenCard :request="request" />
          <RequestRecordCard :request="request" />
        </div>
      </VCol>
    </VRow>

    <RequestDrawer
      v-model:is-drawer-open="isDrawerOpen"
      :form-data="request"
      :mode="drawerMode"
      @refetch-listing="refetchRequest"
    />

    <ReassessDialog
      v-model:is-dialog-open="isReassessDialogOpen"
      :request="request"
      @refetch="refetchRequest"
    />
  </section>
</template>

<style lang="scss" scoped>
@media (min-width: 960px) {
  .request-rail {
    position: sticky;
    inset-block-start: 1.5rem;
  }
}
</style>
